import React, { forwardRef, useId } from 'react';
import classNames from 'classnames';
import { ChevronDown } from 'lucide-react';

interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  label: string;
  options: SelectOption[];
  error?: string;
  fullWidth?: boolean;
  onChange: (value: string) => void;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, options, error, fullWidth = true, className, onChange, id: idProp, ...props }, ref) => {
    const reactId = useId();
    const selectId = idProp ?? `select-${reactId}`;
    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      onChange(e.target.value);
    };

    return (
      <div className={classNames('mb-2', { 'w-full': fullWidth })}>
        {label && (
          <label
            htmlFor={selectId}
            className="block text-[10px] font-semibold text-ink-muted uppercase tracking-widest mb-1.5"
          >
            {label}
          </label>
        )}
        <div className="relative rounded-lg shadow-sm">
          <select
            id={selectId}
            ref={ref}
            className={classNames(
              'block w-full rounded-lg border bg-white py-2 pl-3 pr-8 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-gold-400 focus:border-gold-400 appearance-none transition-colors',
              {
                'border-error-300 focus:ring-error-400 focus:border-error-400': error,
                'border-stone-border': !error,
              },
              className
            )}
            onChange={handleChange}
            {...props}
          >
            {options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <ChevronDown size={15} className="text-ink-faint" />
          </div>
        </div>
        {error && <p className="mt-1 text-xs text-error-600">{error}</p>}
      </div>
    );
  }
);

Select.displayName = 'Select';